// Route -> header title, kept in sync with the sidebar's own label text
// (src/components/usersNavBar/UsersNavBar.jsx) so the header never drifts
// from what the sidebar calls the same page.
export const PAGE_TITLES = {
  "/dashboard": "Dashboard",
  "/statistics": "Statistics",
  "/newOrder": "New Order",
  "/importOrders": "Import Orders",
  "/importRequests": "Import Requests",
  "/ordersList": "Orders List",
  "/cancelledOrders": "Cancelled Orders",
  "/newShippingRequest": "New Request",
  "/tabao1688": "Taobao 1688",
  "/shippingRequests": "Sourcing Requests",
  "/userSourcingRestockRequests": "Restock Requests",
  "/productsList": "Products List",
  "/integrations": "Connected Stores",
  "/integrations/fixes": "Store Fixes",
  "/newClient": "New Client",
  "/clientsList": "Clients List",
  "/wallet": "Wallet",
  "/subscriptionPlans": "Subscription",
  "/shippingCalculator": "Shipping Calculator",
  "/notifications": "Notifications",
  "/sellerHistory": "History",
  "/help": "Help & Guide",
};

// Browser-tab titles for the pages outside the app shell (no header or
// sidebar). The first two are what Google shows for the dashboard; the others
// need a link from an email or a session, so they are never indexed.
export const PUBLIC_PAGE_TITLES = {
  "/": "Sourship Dashboard – Sign in",
  "/signUp": "Create your account · Sourship",
  "/forgotPassword": "Reset your password · Sourship",
  "/resetPassword": "Choose a new password · Sourship",
  "/verifyEmail": "Confirm your email · Sourship",
  "/pendingApproval": "Your application · Sourship",
  "/shopifyInstall": "Connect your Shopify store · Sourship",
};

// The header title for an in-app route, or null when it has none listed.
export const pageTitleFor = (pathname) =>
  PAGE_TITLES[pathname] ||
  // One guide per platform under /integrations/help/<platform>, all reached
  // from the Store Fixes page; the slug would otherwise title it "etsy".
  (pathname.startsWith("/integrations/help/") ? PAGE_TITLES["/integrations/fixes"] : null) ||
  // A store's own page (and the links that pick one) sit under Connected
  // Stores; the store's name is the page's own heading.
  (pathname.startsWith("/integrations/") ? PAGE_TITLES["/integrations"] : null);

// What the browser tab (and a search result) says for any route.
export const documentTitleFor = (pathname) => {
  if (PUBLIC_PAGE_TITLES[pathname]) return PUBLIC_PAGE_TITLES[pathname];
  const title = pageTitleFor(pathname);
  return title ? `${title} · Sourship` : "Sourship";
};

// One short line under the page title on phones (components/pageHeading),
// where the header has no room for the title. Only pages without a heading of
// their own are listed: Connected stores, Profile, Help, Taobao 1688, New
// request, the shipping calculator and Notifications draw their own.
export const PAGE_DESCRIPTIONS = {
  "/dashboard": "Your orders, requests and wallet at a glance.",
  "/statistics": "What you sold, what it cost and what you earned.",
  "/newOrder": "Pick a client and products, then pay the shipping.",
  "/importOrders": "Create many orders at once from a spreadsheet.",
  "/importRequests": "Send many sourcing requests at once from a spreadsheet.",
  "/ordersList": "Pay, track or cancel your orders.",
  "/cancelledOrders": "Orders you or our team cancelled.",
  "/shippingRequests": "Follow the products we source for you.",
  "/userSourcingRestockRequests": "Follow the stock we reorder for your products.",
  "/productsList": "Your stock in our warehouse.",
  "/newClient": "Save a customer's address to ship orders to them.",
  "/clientsList": "The customers you ship orders to.",
  "/wallet": "Your balance, top ups and every payment.",
  "/sellerHistory": "Everything done on your account, by you and our team.",
  "/subscriptionPlans": "Your plan, its credits and when it renews.",
};

export const pageDescriptionFor = (pathname) => PAGE_DESCRIPTIONS[pathname] || null;

// The icon in the phone page heading's tile (Font Awesome 6, solid).
export const PAGE_ICONS = {
  "/dashboard": "fa-solid fa-table-columns",
  "/statistics": "fa-solid fa-chart-column",
  "/newOrder": "fa-solid fa-cart-plus",
  "/importOrders": "fa-solid fa-file-import",
  "/importRequests": "fa-solid fa-file-import",
  "/ordersList": "fa-solid fa-bag-shopping",
  "/cancelledOrders": "fa-solid fa-ban",
  "/shippingRequests": "fa-solid fa-box-open",
  "/userSourcingRestockRequests": "fa-solid fa-rotate",
  "/productsList": "fa-solid fa-boxes-stacked",
  "/newClient": "fa-solid fa-user-plus",
  "/clientsList": "fa-solid fa-users",
  "/wallet": "fa-solid fa-wallet",
  "/sellerHistory": "fa-solid fa-clock-rotate-left",
  "/subscriptionPlans": "fa-solid fa-crown",
};

export const pageIconFor = (pathname) => PAGE_ICONS[pathname] || "fa-solid fa-file";

// "Orders List" -> "Orders list": the page heading is in sentence case.
export const sentenceCase = (title) =>
  title ? title.charAt(0).toUpperCase() + title.slice(1).toLowerCase() : "";
